import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DateRangePicker, { DateRange, defaultRange } from './DateRangePicker';
import TimeSeriesChart from './TimeSeriesChart';

/**
 * /reports/clients/:id — everything we have ever sent one buyer.
 *
 * The outbound twin of ReportingSourcePage, and it answers the questions a
 * client relationship actually turns on:
 *
 *   - How many did they get, and what share of attempts landed?
 *   - Which of their destinations are failing? (per-destination table)
 *   - Where is their volume coming from? (per-data-body table, carrying each
 *     body's own SOURCE so you can see the whole path from who sent a lead in
 *     to who got it)
 *   - What does the trend look like? (volume chart)
 *
 * Destinations whose leg-node, leg, or data body has since been DELETED still
 * appear, flagged "deleted". That is the point of the assignment ledger in
 * migration 031: a client's numbers never disappear because someone rebuilt a
 * leg.
 *
 * :id is either a clients.id or the literal 'unassigned' — delivery nodes
 * that predate clients, which are grouped so the totals reconcile.
 */

function fmt(n: number): string {
  return n.toLocaleString();
}

function fmtPct(p: number): string {
  return (p * 100).toFixed(1) + '%';
}

export default function ReportingClientPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { organization } = useAuth();
  const tz = organization?.timezone || undefined;

  const [range, setRange] = useState<DateRange>(() => defaultRange(tz));
  const [data, setData] = useState<api.ReportingClientResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Free-text filter over the destination + data body tables. A long-running
  // buyer accumulates a lot of destinations, and "which of the Acme webhooks
  // is the one on the auto vertical" is the question you actually have.
  const [filter, setFilter] = useState('');
  // Failing destinations first is the default an operator wants; volume order
  // is the one they want when reconciling an invoice.
  const [sort, setSort] = useState<'volume' | 'problems'>('volume');
  const [showDetached, setShowDetached] = useState(true);

  const load = useCallback(async (forceFresh = false) => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getReportingClient(id, range.from, range.to, forceFresh));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load client report');
    } finally {
      setLoading(false);
    }
  }, [id, range.from, range.to]);

  useEffect(() => { void load(false); }, [load]);

  const unassigned = id === api.UNASSIGNED_CLIENT;
  const title = unassigned ? 'Unassigned destinations' : data?.client?.name || 'Client';

  const q = filter.trim().toLowerCase();

  const nodes = useMemo(() => {
    let rows = data?.nodes ?? [];
    if (!showDetached) {rows = rows.filter(n => !n.detached);}
    if (q) {
      rows = rows.filter(n =>
        [n.nodeName, n.nodeType, n.dataBodyName, n.legName]
          .some(v => (v || '').toLowerCase().includes(q)));
    }
    const sorted = [...rows];
    if (sort === 'problems') {
      // Worst delivery rate first, but only among destinations that actually
      // attempted something — a destination with zero traffic isn't a problem,
      // it's just idle, and floating those to the top buries the real ones.
      sorted.sort((a, b) => {
        const aAttempted = a.delivered + a.failed;
        const bAttempted = b.delivered + b.failed;
        if (aAttempted === 0 && bAttempted === 0) {return 0;}
        if (aAttempted === 0) {return 1;}
        if (bAttempted === 0) {return -1;}
        if (a.deliveryRate !== b.deliveryRate) {return a.deliveryRate - b.deliveryRate;}
        return b.failed - a.failed;
      });
    } else {
      sorted.sort((a, b) => b.delivered - a.delivered);
    }
    return sorted;
  }, [data, q, sort, showDetached]);

  const bodies = useMemo(() => {
    const rows = data?.bodies ?? [];
    if (!q) {return rows;}
    return rows.filter(b =>
      [b.dataBodyName, b.sourceName].some(v => (v || '').toLowerCase().includes(q)));
  }, [data, q]);

  const ov = data?.overview;
  const detachedCount = (data?.nodes ?? []).filter(n => n.detached).length;

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ marginBottom: 6 }}>
        <Link to="/reports" style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
          ← All reporting
        </Link>
      </div>
      <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#111827' }}>{title}</h1>
      <p style={{ marginTop: 4, marginBottom: 20, color: '#6b7280', fontSize: 14 }}>
        {unassigned
          ? 'Delivery destinations with no client assigned. Assign one on the node to move its numbers under a buyer.'
          : 'Everything delivered to this client, across every destination they receive from — including destinations that have since been deleted.'}
      </p>

      {!unassigned && data?.client && (
        <ContactCard client={data.client} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <DateRangePicker
          value={range}
          onChange={setRange}
          onRefresh={() => load(true)}
          timezone={tz}
        />
        <input
          className="form-input"
          type="search"
          placeholder="Filter destinations & bodies…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ maxWidth: 260, height: 38 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280' }}>
          <span style={{ fontWeight: 600 }}>Sort</span>
          <select
            className="form-select"
            value={sort}
            onChange={e => setSort(e.target.value as 'volume' | 'problems')}
            style={{ height: 38 }}
          >
            <option value="volume">Most delivered</option>
            <option value="problems">Worst delivery rate</option>
          </select>
        </label>
        {detachedCount > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#6b7280' }}>
            <input
              type="checkbox"
              checked={showDetached}
              onChange={e => setShowDetached(e.target.checked)}
            />
            Include {detachedCount} deleted destination{detachedCount === 1 ? '' : 's'}
          </label>
        )}
      </div>

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      )}

      {ov && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
            <Kpi label="Delivered" value={fmt(ov.delivered)} color="#1d4ed8" />
            <Kpi label="Failed" value={fmt(ov.failed)} color="#b91c1c" />
            <Kpi label="Delivery rate" value={fmtPct(ov.deliveryRate)} color="#047857" />
            <Kpi label="Destinations" value={fmt(ov.destinations)} />
            <Kpi label="Data bodies" value={fmt(ov.bodies)} />
          </div>

          <SectionTitle>Volume over time</SectionTitle>
          <div style={{ marginBottom: 28 }}>
            <TimeSeriesChart data={data!.series} tz={tz} />
          </div>

          <SectionTitle>By destination</SectionTitle>
          <NodesTable rows={nodes} />

          <SectionTitle style={{ marginTop: 32 }}>By data body</SectionTitle>
          <BodiesTable rows={bodies} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: color || '#111827' }}>{value}</div>
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h2 style={{
      margin: '0 0 12px 0',
      fontSize: 16,
      fontWeight: 600,
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      ...style,
    }}>
      {children}
    </h2>
  );
}

function ContactCard({ client }: { client: api.ReportingClientResponse['client'] }) {
  if (!client) {return null;}
  const address = [client.addressLine1, client.addressLine2, client.city, client.state, client.postalCode, client.country]
    .filter(Boolean)
    .join(', ');
  const bits: Array<[string, string | null]> = [
    ['Contact', client.contactName],
    ['Email', client.contactEmail],
    ['Phone', client.contactPhone],
    ['Address', address || null],
  ];
  const shown = bits.filter(([, v]) => !!v);
  if (shown.length === 0 && !client.notes) {return null;}

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {shown.map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {label}
            </div>
            <div style={{ fontSize: 14, color: '#374151', marginTop: 2 }}>{value}</div>
          </div>
        ))}
      </div>
      {client.notes && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6', fontSize: 13, color: '#6b7280', whiteSpace: 'pre-wrap' }}>
          {client.notes}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <Link to="/clients" style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
          Edit in Clients →
        </Link>
      </div>
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: 24,
      background: '#f9fafb',
      border: '1px dashed #e5e7eb',
      borderRadius: 8,
      color: '#9ca3af',
      textAlign: 'center',
    }}>
      {children}
    </div>
  );
}

const NODE_COLS = '1.4fr 1.2fr 120px 120px 120px';

function NodesTable({ rows }: { rows: api.ReportingClientNodeRow[] }) {
  if (rows.length === 0) {
    return <EmptyRow>No deliveries to this client in this range.</EmptyRow>;
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: NODE_COLS,
        padding: '10px 16px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        <div>Destination</div>
        <div>Data Body</div>
        <div style={{ textAlign: 'right' }}>Delivered</div>
        <div style={{ textAlign: 'right' }}>Failed</div>
        <div style={{ textAlign: 'right' }}>Delivery Rate</div>
      </div>
      {rows.map(n => (
        <div key={n.legNodeId} style={{
          display: 'grid',
          gridTemplateColumns: NODE_COLS,
          padding: '12px 16px',
          borderTop: '1px solid #f3f4f6',
          alignItems: 'center',
          fontSize: 14,
          opacity: n.detached ? 0.65 : 1,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {n.nodeName || '(unnamed destination)'}
              {n.detached && (
                <span
                  style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: '#9ca3af' }}
                  title="This destination has been deleted. Its numbers are kept and still count for this client."
                >
                  deleted
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              {[(n.nodeType || '').replace(/_/g, ' '), n.legName].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {n.dataBodyId && n.dataBodyName ? (
              <Link
                to={`/reports/bodies/${n.dataBodyId}`}
                style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
              >
                {n.dataBodyName}
              </Link>
            ) : (
              <span style={{ color: '#d1d5db' }}>{n.dataBodyName || '(deleted body)'}</span>
            )}
          </div>
          <div style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(n.delivered)}</div>
          <div style={{ textAlign: 'right', color: '#b91c1c' }}>{fmt(n.failed)}</div>
          <div style={{
            textAlign: 'right',
            fontWeight: 600,
            // Anything under 90% landed is worth looking at; under 50% is a
            // destination that is effectively down.
            color: n.deliveryRate >= 0.9 ? '#047857' : n.deliveryRate >= 0.5 ? '#b45309' : '#b91c1c',
          }}>
            {fmtPct(n.deliveryRate)}
          </div>
        </div>
      ))}
    </div>
  );
}

const BODY_COLS = '1.2fr 1fr 120px 120px 120px 80px';

function BodiesTable({ rows }: { rows: api.ReportingClientBodyRow[] }) {
  if (rows.length === 0) {
    return <EmptyRow>No data body activity for this client in this range.</EmptyRow>;
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: BODY_COLS,
        padding: '10px 16px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        <div>Data Body</div>
        <div>Source (who sent it in)</div>
        <div style={{ textAlign: 'right' }}>Delivered</div>
        <div style={{ textAlign: 'right' }}>Failed</div>
        <div style={{ textAlign: 'right' }}>Delivery Rate</div>
        <div />
      </div>
      {rows.map(b => (
        <div key={b.dataBodyId} style={{
          display: 'grid',
          gridTemplateColumns: BODY_COLS,
          padding: '12px 16px',
          borderTop: '1px solid #f3f4f6',
          alignItems: 'center',
          fontSize: 14,
        }}>
          <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {b.dataBodyName || <span style={{ color: '#9ca3af' }}>(deleted body)</span>}
          </div>
          <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {b.sourceId && b.sourceName ? (
              <Link
                to={`/reports/sources/${b.sourceId}`}
                style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
              >
                {b.sourceName}
              </Link>
            ) : (
              <span style={{ color: '#d1d5db' }}>Unassigned</span>
            )}
          </div>
          <div style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(b.delivered)}</div>
          <div style={{ textAlign: 'right', color: '#b91c1c' }}>{fmt(b.failed)}</div>
          <div style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtPct(b.deliveryRate)}</div>
          <div style={{ textAlign: 'right' }}>
            <Link
              to={`/reports/bodies/${b.dataBodyId}`}
              style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
            >
              Drill →
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
